import { defineStore } from 'pinia';
import apolloClient from 'utils/apolloClient';
import Vue from 'vue';
import { getTimeRanges } from 'services/boxCatalogView';
import dayjs from 'dayjs';

import { INITIAL_STATE_BOX_ACTIVITIES } from 'constants/initialState';

import ElasticPaginatedActivities from 'assets/graphql/queries/ElasticPaginatedActivities.graphql';

import ActivityBySlug from 'assets/graphql/queries/Activity.graphql';

import {
  hasValue,
  createTagsForFacet,
  processPriceFilter,
  hasValidFilters,
  urlCallbackManager,
} from 'utils/filterHelpers';

export const useCatalogFiltersStore = defineStore('catalogFilters', {
  state: () => ({
    currentFilters: [{ ...INITIAL_STATE_BOX_ACTIVITIES }],
    facets: [],
    results: [],
    page_info: { number: 1 },
    allResultsCount: { 0: 0 },
    loading: false,
    cardSelected: 0,
    orderBy: 'relevance',
    orderType: 'box_catalog',
    boxIdBackup: null,
    urlFilters: null,
    flags: {
      showReservationModal: false,
      showPreviewModal: false,
      loading: false,
    },
    searchByDateTimeConfig: {
      allTimeRanges: [],
      timeRangeSlug: '',
      selectedSchedule: null,
      selectedDate: null,
    },
    activityPreview: null,
    catalogCodeComponent: null,
    catalogModalComponent: null,
    keywords: '',
    isInitializing: false,
    shouldUpdateURL: true,
    activeRequestController: null,
    requestCounter: 0,
    _isInsideLoadingFetch: false,
    _dateTimeDebounceTimeout: null,
  }),

  getters: {
    getFacets: state => state.facets,
    getCurrentFilters: state => state.currentFilters,
    getAllResultsCount: state => state.allResultsCount,
    getCardSelected: state => state.cardSelected,
    getPageInfo: state => state.page_info,
    getResults: state => state.results,
    getLoading: state => state.loading,
    getCurrentOrder: state => state.orderBy,
    getModalReservationFlag: state => state.flags.showReservationModal,
    getModalPreviewFlag: state => state.flags.showPreviewModal,
    getActivityPreview: state => state.activityPreview,
    getCatalogCodeComponent: state => state.catalogCodeComponent,
    getCatalogModalComponent: state => state.catalogModalComponent,
    getKeywords: state => state.keywords,

    sendableFilters: state => {
      if (!state.currentFilters || !state.currentFilters[state.cardSelected]) {
        return {};
      }

      const filters = { ...state.currentFilters[state.cardSelected] };

      if (state.searchByDateTimeConfig.selectedDate) {
        filters.date = state.searchByDateTimeConfig.selectedDate;
      }

      if (state.searchByDateTimeConfig.timeRangeSlug) {
        filters.time_range_slug = state.searchByDateTimeConfig.timeRangeSlug;
      }

      filters.price = processPriceFilter(filters.price);

      if (state.keywords && state.keywords.trim() !== '') {
        filters.keywords = state.keywords;
      }

      if (state.boxIdBackup) {
        filters.box_ids = [Number(state.boxIdBackup)];
      }

      return filters;
    },

    getCurrentTags: state => {
      const { currentFilters, facets, cardSelected } = state;
      const currentFilter = currentFilters[cardSelected];

      if (!currentFilter) return [];

      const facetTags = Object.entries(currentFilter).reduce(
        (tags, [key, value]) => {
          if (!hasValue(value)) return tags;

          const facet = facets.find(f => f.name === key);
          if (!facet) return tags;

          return [...tags, ...createTagsForFacet(facet, value, key)];
        },
        [],
      );

      return [...facetTags];
    },

    getTitleDate: state => {
      const { selectedDate } = state.searchByDateTimeConfig;
      if (!selectedDate) return null;

      const date = dayjs(selectedDate);
      const formattedDateName = date.format('dddd');
      const capitalizedDateName =
        formattedDateName.charAt(0).toUpperCase() + formattedDateName.slice(1);
      const formattedDateNumber = date.format('DD/MM');
      return `${capitalizedDateName} ${formattedDateNumber}`;
    },
    getSelectedSchedule: state => state.searchByDateTimeConfig.selectedSchedule,
    getFormattedDate: state => {
      return state.searchByDateTimeConfig.selectedDate
        ? dayjs(state.searchByDateTimeConfig.selectedDate).format('YYYY-MM-DD')
        : null;
    },
    getAllTimeRanges: state => state.searchByDateTimeConfig.allTimeRanges,
    getOrderType: ({ orderType }) => orderType,
  },

  actions: {
    async init(initialKeywords = null) {
      try {
        this.isInitializing = true;

        this.currentFilters = this.resetCurrentFilters();
        this._resetPagination();
        if (initialKeywords !== null) {
          this.keywords = initialKeywords;
        } else {
          this.keywords = '';
        }
        this.orderBy = 'relevance';

        await this.getTimeRanges();
      } catch (err) {
        /* eslint-disable-next-line */
        console.error('Error on boxCatalogView store init', err);
      } finally {
        this.isInitializing = false;
      }
    },

    async updateFilters(updates = {}) {
      const { key, inputType, value, orderBy: newOrderBy } = updates;

      try {
        this.loading = true;

        if (newOrderBy) {
          this.orderBy = newOrderBy;
        }

        if (key && inputType) {
          this._updateFilterValue(key, inputType, value);
        }

        this._resetPagination();

        await this._fetchCurrentResults();

        if (this._hasValidFilters()) {
          await this._updateAllResultCounts();
        }

        if (!this.isInitializing && this.shouldUpdateURL) {
          this._updateURLFromFilters();
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error updating filters:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async removeTag({ key, inputType, value }) {
      if (key === 'date') {
        this.onSetDate('', false);
        await this.updateFilters();
        return;
      }

      if (key === 'time_range_slug') {
        this.onSetTimeSlot('', false);
        await this.updateFilters();
        return;
      }

      if (inputType === 'radio' || inputType === 'toggle') {
        this._setFilterValue(key, []);
      } else if (inputType === 'checkbox') {
        const currentValues = this.currentFilters[this.cardSelected][key] || [];
        const newValues = currentValues.filter(v => v !== value);
        this._setFilterValue(key, newValues);
      }

      await this.updateFilters();
    },

    async getTimeRanges() {
      this.searchByDateTimeConfig.allTimeRanges = await getTimeRanges();
      this.searchByDateTimeConfig.allTimeRanges.sort((a, b) => {
        const startTimeA = parseInt(a.start.split(':')[0]);
        const startTimeB = parseInt(b.start.split(':')[0]);
        return startTimeA - startTimeB;
      });
    },

    setKeywords(keywords, shouldFetch = true, shouldUpdateURL = true) {
      this.keywords = keywords;

      if (!this.isInitializing && this.shouldUpdateURL && shouldUpdateURL) {
        this._updateURLFromFilters();
      }

      if (shouldFetch) {
        this._setLoadingAndFetch(() => this._fetchCurrentResults());
      }
    },

    async setBoxCategory(category) {
      Vue.set(this.currentFilters[0], 'category_name', category);
    },

    async setCard(cardId) {
      this.cardSelected = cardId ?? 0;
    },

    async clearCurrentFilters(options = {}) {
      const { clearDateTime = false, refetch = true } = options;

      this._cancelActiveRequest();

      const currentBoxId = this.boxIdBackup;

      this.currentFilters = this.resetCurrentFilters();
      this._resetPagination();
      this.keywords = '';
      this.orderBy = 'relevance';

      if (currentBoxId) {
        Vue.set(this.currentFilters[0], 'box_ids', [Number(currentBoxId)]);
      }

      if (clearDateTime) {
        this.searchByDateTimeConfig.selectedDate = null;
        this.searchByDateTimeConfig.timeRangeSlug = '';
        this.searchByDateTimeConfig.selectedSchedule = null;
      }

      if (!this.isInitializing && this.shouldUpdateURL) {
        this._updateURLFromFilters();
      }

      if (refetch) {
        try {
          this.loading = true;
          await this._fetchCurrentResults();
          // Update URL after successful fetch
          if (!this.isInitializing && this.shouldUpdateURL) {
            this._updateURLFromFilters();
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(
            'Error fetching results after clearing filters:',
            error,
          );
          throw error;
        } finally {
          this.loading = false;
        }
      }
    },

    resetCurrentFilters() {
      return [
        {
          ...INITIAL_STATE_BOX_ACTIVITIES,
        },
      ];
    },

    setBoxId(boxId, shouldFetch = true, shouldUpdateURL = true) {
      this.boxIdBackup = boxId;
      Vue.set(this.currentFilters[0], 'box_ids', boxId ? [Number(boxId)] : []);

      if (!this.isInitializing && this.shouldUpdateURL && shouldUpdateURL) {
        this._updateURLFromFilters();
      }

      if (shouldFetch) {
        this.clearCurrentFilters({
          refetch: true,
          clearDateTime: true,
        });
      }
    },

    async updatePageNumber(pageNumber) {
      this.page_info.number = pageNumber;

      await this._setLoadingAndFetch(() => this._fetchCurrentResults());
    },

    resetPaginationNumber() {
      this._resetPagination();
    },

    setCatalogOrderType(orderType) {
      this.orderType = orderType;
    },

    _updateFilterValue(key, inputType, value) {
      this._setFilterValue(key, value);
    },

    _setFilterValue(key, value) {
      Vue.set(this.currentFilters[this.cardSelected], key, value);
    },

    _resetPagination() {
      this.page_info.number = 1;
    },

    _hasValidFilters() {
      return hasValidFilters(this.sendableFilters);
    },

    async _fetchCurrentResults() {
      this._cancelActiveRequest();
      this.activeRequestController = new AbortController();
      const requestId = ++this.requestCounter;

      const variables = this.getVariablesForQuery();

      try {
        const { data } = await apolloClient.query({
          query: this.getQuery(),
          variables,
          fetchPolicy: 'no-cache',
          context: {
            fetchOptions: {
              signal: this.activeRequestController.signal,
            },
          },
        });

        if (
          requestId === this.requestCounter &&
          !this.activeRequestController.signal.aborted
        ) {
          const result = data[this.getPaginatedName()];

          this.results = result.results;
          this.facets = result.facets;
          this.page_info = result.page_info;
          this.allResultsCount[this.cardSelected] = result.page_info.count;

          this._updateDynamicSubfilters();
        }
      } catch (error) {
        if (
          error.name !== 'AbortError' &&
          !this.activeRequestController?.signal?.aborted
        ) {
          throw error;
        }
      } finally {
        if (requestId === this.requestCounter) {
          this.activeRequestController = null;
          if (!this.isInitializing && this.shouldUpdateURL) {
            this._updateURLFromFilters();
          }
        }
      }
    },

    _updateDynamicSubfilters() {
      this.facets.forEach(facet => {
        if (facet.name.includes('subfilter_')) {
          const currentValue =
            this.currentFilters[this.cardSelected][facet.name] || [];
          Vue.set(
            this.currentFilters[this.cardSelected],
            facet.name,
            currentValue,
          );
        }
      });
    },

    async _updateAllResultCounts() {},

    getQuery(index = this.cardSelected) {
      return [ElasticPaginatedActivities][index];
    },

    getVariablesForQuery() {
      return {
        filters: JSON.stringify(this.sendableFilters),
        order_by: this.orderBy,
        page_number: this.page_info.number,
        per_page: 12,
        view: this.orderType,
      };
    },

    getPaginatedName(index = this.cardSelected) {
      return ['elastic_paginated_activities'][index];
    },

    deleteTags({ key, inputType, value }) {
      return this.removeTag({ key, inputType, value });
    },

    async handleChangeFilters(key, type) {
      if (type === 'orderBy') {
        await this.updateFilters({ orderBy: key });
      } else {
        await this.updateFilters({ key, inputType: type });
      }
    },

    async getDataFromFilters() {
      if (this._isInsideLoadingFetch) {
        return await this._fetchCurrentResults();
      }
      return await this._setLoadingAndFetch(() => this._fetchCurrentResults());
    },

    applyURLFilters(urlFilters) {
      this.shouldUpdateURL = false;

      try {
        if (Array.isArray(urlFilters) && urlFilters.length > 0) {
          const newFilter = urlFilters[0];
          if (newFilter && typeof newFilter === 'object') {
            Vue.set(this.currentFilters, 0, { ...newFilter });

            if (newFilter.date) {
              this.searchByDateTimeConfig.selectedDate = newFilter.date;
            }
            if (newFilter.time_range_slug) {
              this.searchByDateTimeConfig.timeRangeSlug =
                newFilter.time_range_slug;
            }
          } else {
            Vue.set(this.currentFilters, 0, {
              ...INITIAL_STATE_BOX_ACTIVITIES,
            });
          }
        }
      } finally {
        this.shouldUpdateURL = true;
      }
    },

    onToggleModalReservation() {
      this.flags.showReservationModal = !this.flags.showReservationModal;
    },

    onToggleModalPreview(activityObject) {
      this.flags.showPreviewModal = !this.flags.showPreviewModal;
      this.activityPreview = activityObject;
    },

    async onActivityBySlug(slug) {
      try {
        this.flags.loading = true;

        let activity;

        if (slug) {
          const { data } = await apolloClient.query({
            query: ActivityBySlug,
            variables: { slug },
          });

          activity = data.activity;
        } else {
          activity = this.activityPreview;
        }

        const { breadcrumb, catalog_case, is_catalog, gift_information } =
          activity.activity_view_config;
        const breadCrumb = JSON.parse(breadcrumb);
        const giftInformation = JSON.parse(gift_information);
        const catalogCase = JSON.parse(catalog_case);
        const isCatalog = JSON.parse(is_catalog);
        const isReserved = ['reserved', 'validated_and_not_reserved'].includes(
          giftInformation.status,
        );
        activity.locations =
          activity.partneractivity?.locations ||
          activity.comboactivity.locations;

        if (isCatalog) return;
        if (!catalogCase) return;

        this.catalogCodeComponent = {
          name: catalogCase.code_component,
          props: {
            code: catalogCase.code,
            activity,
            catalogColor: breadCrumb.color,
            reservedGift: giftInformation,
            isReserved,
          },
        };

        this.catalogModalComponent = {
          name: catalogCase.modal_component,
          props: {
            title: 'Reservá esta experiencia',
            code: catalogCase.code,
            codeId: catalogCase.code_id,
            categoryColor: breadCrumb.color,
            locations: activity.locations,
            activity,
            isReserved,
            reserverdGift: giftInformation,
            codeExpirationDate: giftInformation.expiration_date,
          },
        };
        return activity;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in onActivityBySlug:', error);
        return error;
      } finally {
        this.flags.loading = false;
      }
    },

    onSetTimeSlot(timeSlotSlug, shouldFetch = true, debounceMs = 200) {
      const previousTimeSlug = this.searchByDateTimeConfig.timeRangeSlug;

      this.searchByDateTimeConfig.timeRangeSlug = timeSlotSlug;

      if (!this.isInitializing && this.shouldUpdateURL) {
        this._updateURLFromFilters();
      }

      const timeSlugChanged = previousTimeSlug !== timeSlotSlug;

      if (shouldFetch && (timeSlotSlug !== '' || timeSlugChanged)) {
        this._debouncedDateTimeFetch(debounceMs);
      }
    },

    onSetDate(selectedDate, shouldFetch = false, debounceMs = 300) {
      const previousDate = this.searchByDateTimeConfig.selectedDate;

      this.searchByDateTimeConfig.selectedDate = selectedDate
        ? dayjs(selectedDate).format('YYYY-MM-DD')
        : '';

      if (!this.isInitializing && this.shouldUpdateURL) {
        this._updateURLFromFilters();
      }

      const hasTimeSlug = Boolean(this.searchByDateTimeConfig.timeRangeSlug);
      const dateChanged =
        previousDate !== this.searchByDateTimeConfig.selectedDate;
      const shouldRefetch =
        (shouldFetch && hasTimeSlug) || (dateChanged && hasTimeSlug);

      if (shouldRefetch) {
        this._debouncedDateTimeFetch(debounceMs);
      }
    },

    onSetSchedule(schedule) {
      this.searchByDateTimeConfig.selectedSchedule = schedule;
    },

    async cleanAvailability() {
      this._clearDateTimeDebounce();

      this.searchByDateTimeConfig.selectedDate = null;
      this.searchByDateTimeConfig.timeRangeSlug = '';
      this.searchByDateTimeConfig.selectedSchedule = null;

      if (!this.isInitializing && this.shouldUpdateURL) {
        this._updateURLFromFilters();
      }

      try {
        this.loading = true;
        await this._fetchCurrentResults();
        if (!this.isInitializing && this.shouldUpdateURL) {
          this._updateURLFromFilters();
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          'Error fetching results after clearing availability:',
          error,
        );
        throw error;
      } finally {
        this.loading = false;
      }
    },

    _updateURLFromFilters() {
      urlCallbackManager.markCatalogFiltersURLUpdateNeeded(this.currentFilters);
    },

    registerURLUpdateCallback(callback) {
      urlCallbackManager.register(callback, 'catalogFilters');
    },

    unregisterURLUpdateCallback() {
      urlCallbackManager.unregister('catalogFilters');
    },

    async _setLoadingAndFetch(fetchFunction, forceExecution = false) {
      if (this._isInsideLoadingFetch && !forceExecution) {
        return await fetchFunction();
      }

      if (!forceExecution && this.loading) {
        return;
      }

      if (forceExecution) {
        this._cancelActiveRequest();
        this._isInsideLoadingFetch = false;
        this.loading = false;
      } else if (this.loading) {
        return;
      }

      try {
        this._isInsideLoadingFetch = true;
        this.loading = true;
        const result = await fetchFunction();
        return result;
      } catch (error) {
        if (error.name !== 'AbortError') {
          // eslint-disable-next-line no-console
          console.error('Error in _setLoadingAndFetch:', error);
          throw error;
        }
      } finally {
        this._isInsideLoadingFetch = false;
        this.loading = false;
      }
    },

    _cancelActiveRequest() {
      if (this.activeRequestController) {
        // this.activeRequestController.abort();
        this.activeRequestController = null;
        if (this.loading) {
          this.loading = false;
        }
      }
    },

    _debouncedDateTimeFetch(debounceMs = 300) {
      if (this._dateTimeDebounceTimeout) {
        clearTimeout(this._dateTimeDebounceTimeout);
        this._dateTimeDebounceTimeout = null;
      }
      this._dateTimeDebounceTimeout = setTimeout(() => {
        this._dateTimeDebounceTimeout = null;
        this._setLoadingAndFetch(() => this._fetchCurrentResults());
      }, debounceMs);
    },

    _clearDateTimeDebounce() {
      if (this._dateTimeDebounceTimeout) {
        clearTimeout(this._dateTimeDebounceTimeout);
        this._dateTimeDebounceTimeout = null;
      }
    },

    // Public method to cancel requests when component unmounts or routes change
    cancelAllRequests() {
      this._cancelActiveRequest();
      this._clearDateTimeDebounce();
      this.requestCounter = 0;
      this.loading = false;
    },
  },
});
